// The software policy page: what to ask cma/softwarepolicy for, and how to turn what a person typed into the document the API stores
// (and back). Plain functions, no React, so they can be unit tested. The API checks every document again; the checks here only save a
// round trip and say what is wrong in plain words.
//
// The policy works the way the VES endpoint schedules do. A schedule has a filter (label is value, all must match) and the scripts to
// run on the devices that match; a rule set has a filter and the rules (a program is required, allowed or forbidden) for the machines
// that match. A rule set is identified by its filter: "the whole account" is the filter account is myorg.

import { UNINSTALL_SCRIPT, UPDATE_SCRIPT } from '../SoftwareBuilder/builderLogic';

export const POLICY_URL = 'cma/softwarepolicy';
export const GLOBAL = '*';

export const MODES = [
  { value: 'required', label: 'Required' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'forbidden', label: 'Forbidden' },
];
export const UNLISTED = [
  { value: 'ignore', label: 'Ignore it' },
  { value: 'report', label: 'Report it' },
];
// The scripts a definition can name. Installing is not one of them: a program is installed by winget (Install-WingetSoftware) and only by winget.
export const ACTIONS = [
  { name: 'update', label: 'Update' },
  { name: 'uninstall', label: 'Remove' },
];

export const devicesUrl = (account) => `${POLICY_URL}?${new URLSearchParams({ account, kind: 'devices' }).toString()}`;
export const policyUrl = (account) => `${POLICY_URL}?${new URLSearchParams({ account }).toString()}`;
export const historyUrl = (account, limit = 100) => `${POLICY_URL}?${new URLSearchParams({ account, kind: 'history', limit: String(limit) }).toString()}`;
// The builder page of one rule set: where its software is chosen.
export const builderUrl = (account, id) => `/softwarebuilder?${new URLSearchParams(id ? { account, ruleset: id } : { account }).toString()}`;
export const deleteUrl = (account, id) => `${POLICY_URL}?${new URLSearchParams({ account, id }).toString()}`;

// What to tell the person when a request failed: the API's own reason when it gave one.
export const errorText = (ex) => ex?.response?.data?.error?.message || ex?.toString?.() || 'The request failed';

const list = (v) => (Array.isArray(v) ? v : []);

// An account whose policy is still in the files that ship with the app cannot be edited here: the API refuses (409) until it is
// moved into the database. A new account that has nothing yet can be.
export const rulesEditable = (data) => data?.policySource === 'database' || list(data?.ruleSets).length === 0;
export const definitionsEditable = (data) => data?.definitionsSource === 'database';

export const FILES_NOTE = 'This account\'s policy is still in the files that ship with the app, so it can only be read here. '
  + 'Move it into the database first (tools/seed_softwarepolicy.py in cma-api).';
export const DEFINITIONS_FILES_NOTE = 'The program definitions are still in the files that ship with the app, so they can only be read here. '
  + 'Move them into the database first (tools/seed_softwarepolicy.py in cma-api).';

// ---- filters (the way a schedule's filter reads) --------------------------------------------------------------------------

// Does a machine with these labels match the filters? Every one must, with the label name and the value in any case, as the API matches.
export const filterMatches = (filter, labels) => {
  const wanted = list(filter).filter((f) => f.label && f.value);
  if (!wanted.length) return false;
  const have = {};
  Object.entries(labels || {}).forEach(([k, v]) => { if (v !== null && typeof v !== 'undefined') have[String(k).toLowerCase()] = String(v).trim().toLowerCase(); });
  return wanted.every((f) => have[f.label.trim().toLowerCase()] === f.value.trim().toLowerCase());
};

// The machines (from the VES list) whose labels match the filters now: what the person sees before saving.
export const machinesMatching = (filter, devices) => list(devices).filter((d) => filterMatches(filter, d.labels));

// "account is myorg, software is developer": how a rule set is named.
export const filterText = (filter) => list(filter).map((f) => `${f.label} is ${f.value}`).join(', ');

// The labels the machines carry and the values each has, to suggest while a filter is typed.
export const labelChoices = (devices) => {
  const names = new Map();
  list(devices).forEach((d) => Object.entries(d.labels || {}).forEach(([k, v]) => {
    if (v === null || typeof v === 'undefined' || typeof v === 'object') return;
    if (!names.has(k)) names.set(k, new Set());
    names.get(k).add(String(v));
  }));
  return Object.fromEntries([...names.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, [...v].sort()]));
};

// What a rule set is called in a list: its description, else its filter.
export const ruleSetName = (rs) => (rs?.description && rs.description.trim()) || filterText(rs?.filter) || 'Rule set';

// Why the rule sets that apply to a machine are the ones they are, in plain words (labelsSource of the comparison).
export const LABELS_TEXT = {
  ves: 'The labels VES has for this machine.',
  'unknown-device': 'VES does not list this machine, so only the rule sets for the whole account can be told to apply.',
  unavailable: 'The machine list could not be read from VES, so only the rule sets for the whole account can be told to apply.',
  none: 'There is no software policy for this account.',
};

// ---- rule sets -----------------------------------------------------------------------------------------------------------

export const emptyRuleRow = () => ({ key: '', mode: 'required', reason: '', enabled: true });

// A new rule set starts as the whole account: account is <the account>. Remove or change the filter to make it more specific.
export const emptyRuleSet = (account) => ({ description: '', enabled: true, filter: [{ label: 'account', value: account || '' }], rules: [emptyRuleRow()] });

export const ruleSetToForm = (rs) => ({
  description: rs.description || '', enabled: rs.enabled !== false,
  filter: list(rs.filter).length ? rs.filter.map((f) => ({ label: f.label || '', value: f.value || '' })) : [{ label: '', value: '' }],
  rules: list(rs.rules).map((r) => ({ key: r.key || '', mode: r.mode || 'required', reason: r.reason || '', enabled: r.enabled !== false })),
});

export const ruleSetBody = (account, form) => ({
  account, kind: 'ruleset', description: form.description.trim(), enabled: form.enabled,
  filter: form.filter.filter((f) => f.label.trim() || f.value.trim()).map((f) => ({ label: f.label.trim(), value: f.value.trim() })),
  rules: form.rules.filter((r) => r.key.trim()).map((r) => ({ key: r.key.trim(), mode: r.mode, reason: r.reason.trim(), enabled: r.enabled })),
});

// '' when the form is fine, else what to fix. knownKeys: the programs that have a definition.
export const ruleSetProblem = (form, knownKeys) => {
  const filters = form.filter.filter((f) => f.label.trim() || f.value.trim());
  if (filters.length === 0) return 'Add at least one filter: a label and the value it must have (account is myorg is the whole account).';
  if (filters.length > 10) return 'A rule set can have at most 10 filters.';
  if (filters.some((f) => !f.label.trim() || !f.value.trim())) return 'Each filter needs a label and a value.';
  const names = filters.map((f) => f.label.trim().toLowerCase());
  if (new Set(names).size !== names.length) return 'A label can only be in the filter once: a machine cannot have two values of one label.';
  const rules = form.rules.filter((r) => r.key.trim());
  if (form.rules.some((r) => !r.key.trim() && (r.reason.trim()))) return 'A rule with a reason needs a program.';
  const keys = rules.map((r) => r.key.trim());
  if (new Set(keys).size !== keys.length) return 'A program can only have one rule in a rule set.';
  const unknown = keys.find((k) => knownKeys && knownKeys.length && !knownKeys.includes(k));
  if (unknown) return `${unknown} has no definition yet: add it on the Programs tab first.`;
  return '';
};

// ---- settings ------------------------------------------------------------------------------------------------------------

export const settingsToForm = (s) => ({ unlistedSoftware: s?.unlistedSoftware || 'ignore' });
export const settingsBody = (account, form) => ({ account, kind: 'settings', unlistedSoftware: form.unlistedSoftware });

// ---- definitions ---------------------------------------------------------------------------------------------------------

const json = (value) => (value && Object.keys(value).length ? JSON.stringify(value) : '');

export const emptyDefinition = () => ({
  scope: 'account', key: '', displayName: '', note: '', namePattern: '', wingetId: '',
  actions: Object.fromEntries(ACTIONS.map(({ name }) => [name, { enabled: false, script: '', splat: '', dryRunSplat: '', applySplat: '', requiresApproval: false }])),
});

// Where the standard scripts used to be (cma/endpoint/software). A definition saved before they moved may still name the old path: it is
// shown, and saved again, as the new one. The API reads an old path the same way, so a definition that is never opened keeps working.
const MOVED_SCRIPTS = {
  'cma/endpoint/software/update-wingetsoftware.ps1': UPDATE_SCRIPT,
  'cma/endpoint/software/uninstall-software.ps1': UNINSTALL_SCRIPT,
};
export const currentScript = (path) => MOVED_SCRIPTS[String(path || '').trim().toLowerCase()] || path;

export const definitionToForm = (def) => ({
  scope: def.account === GLOBAL ? 'global' : 'account', key: def.key || '', displayName: def.displayName || '', note: def.note || '',
  namePattern: def.detect?.namePattern || '', wingetId: def.detect?.wingetId || '',
  actions: Object.fromEntries(ACTIONS.map(({ name }) => {
    const a = def.actions?.[name];
    return [name, a ? { enabled: true, script: currentScript(a.script) || '', splat: json(a.splat), dryRunSplat: json(a.dryRunSplat), applySplat: json(a.applySplat), requiresApproval: a.requiresApproval === true }
      : { enabled: false, script: '', splat: '', dryRunSplat: '', applySplat: '', requiresApproval: false }];
  })),
});

// Parameters are typed as JSON, like {"WingetId": "Git.Git"}. Blank means none. Throws an Error that says what is wrong.
export const parseParams = (text, label) => {
  const t = (text || '').trim();
  if (!t) return {};
  let value;
  try {
    value = JSON.parse(t);
  } catch (ex) {
    throw new Error(`${label} is not valid JSON. Write it like {"DryRun": true}.`);
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label} must be a JSON object, like {"DryRun": true}.`);
  return value;
};

export const definitionBody = (account, form) => {
  const actions = { install: null };               // installing is winget's: a saved definition keeps no install script of its own
  ACTIONS.forEach(({ name, label }) => {
    const a = form.actions[name];
    actions[name] = !a.enabled ? null : {
      script: a.script.trim(),
      splat: parseParams(a.splat, `${label}: parameters`),
      dryRunSplat: parseParams(a.dryRunSplat, `${label}: parameters for the check`),
      applySplat: parseParams(a.applySplat, `${label}: parameters for the change`),
      requiresApproval: a.requiresApproval,
    };
  });
  return {
    account: form.scope === 'global' ? GLOBAL : account, kind: 'definition', key: form.key.trim(), displayName: form.displayName.trim(), note: form.note.trim(),
    detect: { namePattern: form.namePattern.trim(), wingetId: form.wingetId.trim() || null }, actions,
  };
};

export const definitionProblem = (form) => {
  if (!form.key.trim()) return 'Give the program a key (its winget id, for example Git.Git).';
  if (!form.namePattern.trim()) return 'Say how to recognise the program: a pattern for its installed name, for example ^Git$.';
  try {
    // eslint-disable-next-line no-new
    new RegExp(form.namePattern);
  } catch (ex) {
    return 'The name pattern is not a valid regular expression.';
  }
  const missing = ACTIONS.find(({ name }) => form.actions[name].enabled && !form.actions[name].script.trim());
  if (missing) return `${missing.label}: give the script, or untick it if there is none yet.`;
  try {
    definitionBody('x', form);
  } catch (ex) {
    return ex.message;
  }
  return '';
};

// "Update-WingetSoftware.ps1" for an action, or "none yet".
export const actionSummary = (action, definition, name) => {
  if (action?.script) return action.script.split('/').pop();
  // A program with a winget id is updated and installed by the standard winget scripts, and any program is removed with Uninstall-Software.
  if (definition?.detect?.wingetId && name === 'update') return 'Update-WingetSoftware.ps1 (standard)';
  if (definition && name === 'uninstall') return 'Uninstall-Software.ps1 (standard)';
  return 'none yet';
};

// ---- history -------------------------------------------------------------------------------------------------------------

export const historyWhat = (h) => {
  const doc = h.after || h.before || {};
  const kind = h.docKind || doc.kind || 'document';
  let what = kind;
  if (kind === 'ruleset') what = `rule set: ${ruleSetName(doc)}`;
  else if (kind === 'definition') what = `program: ${doc.key}`;
  else if (kind === 'settings') what = 'settings';
  // from before rule sets were identified by their filters
  else if (kind === 'rule') what = `rule: ${doc.key}`;
  else if (kind === 'profile') what = `profile: ${doc.name}`;
  else if (kind === 'assignment') what = `machine: ${doc.hostname || doc.deviceId}`;
  return what;
};

export const historyChange = (h) => {
  if (h.action === 'delete') return 'Deleted';
  return h.before ? 'Changed' : 'Added';
};
